import axios from "axios";
import route from "ziggy-js";
import { sortBy } from "lodash";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";

const QUERY_KEY = ["tags"];

async function createTag({ name, tenants }) {
    const { data: r } = await axios.post(route("api.tags.create"), {
        name,
        tenant_id: tenants[0],
    });
    return r.data;
}

export function useCreateTag() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createTag,
        onMutate: async (newTag) => {
            await queryClient.cancelQueries({ queryKey: QUERY_KEY });

            const previousTags = queryClient.getQueryData(QUERY_KEY);

            queryClient.setQueryData(QUERY_KEY, (old) => {
                const tags = old ? [...old] : [];

                const tagIndex = tags.findIndex(
                    (tag) => tag.name === newTag.name
                );

                if (tagIndex !== -1) {
                    tags[tagIndex] = {
                        ...tags[tagIndex],
                        tenants: [
                            ...new Set([
                                ...tags[tagIndex].tenants,
                                ...newTag.tenants,
                            ]),
                        ],
                    };
                } else {
                    tags.push(newTag);
                }

                return sortBy(tags, "name");
            });

            return { previousTags };
        },
        onError: (err, _, context) => {
            queryClient.setQueryData(QUERY_KEY, context.previousTags);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        },
    });
}

async function fetchTags() {
    const { data: r } = await axios.get(route("api.tags.all"));
    return r.data;
}

export function useTags(tenantIds = [], filterType = "all") {
    return useQuery({
        queryKey: QUERY_KEY,
        queryFn: fetchTags,
        refetchOnMount: true,
        select: (data) => filterTags(data, tenantIds, filterType),
    });
}

const filterTags = (tags, tenantIds, filterType = "all") => {
    if (!tenantIds.length) {
        return sortBy(tags, "name");
    }
    return sortBy(
        tags.filter((tag) =>
            tenantIds[getFilterFn(filterType)]((tenantId) =>
                tag.tenants.includes(tenantId)
            )
        ),
        "name"
    );
};

function getFilterFn(filterType) {
    switch (filterType) {
        case "any":
            return "some";
        case "all":
        default:
            return "every";
    }
}
